import React, { useState } from 'react';
import { useTranslation, formatNumber } from '../../../../shared/i18n';
import { getFeatureComparison } from '../../../../shared/config/planFeatures';
import { SUBSCRIPTION_COLORS } from '../subscriptionColors';
import { GiftCodeSection } from '../GiftCodeSection';

export const WizardSubscriptionStep = ({
  child,
  family,
  onContinueWithFree,
  onContinue,
  onBack,
  activityCounts = null
}) => {
  const { t } = useTranslation();
  const [isYearly, setIsYearly] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSubscribed = child.subscribed;

  const formatCurrency = (amount) => {
    return formatNumber(amount) + '\u20AB';
  };

  const hasCashback = family.cashback_percentage > 0;
  const discountInfo = family.discount_percentage > 0
    ? t(`pricing_models.${family.pricing_model}`)
    : null;

  // Get pricing for a package and period
  const getPrice = (pkg, period) => {
    if (pkg === 'free') return 0;
    const prefix = pkg === 'premium' ? 'premium_' : '';
    return family[`${prefix}${period}_payment_amount`];
  };

  const getPriceBeforeCashback = (pkg, period) => {
    if (pkg === 'free') return 0;
    const prefix = pkg === 'premium' ? 'premium_' : '';
    return family[`${prefix}${period}_payment_amount_before_cashback`];
  };

  const getCashbackAmount = (pkg, period) => {
    if (pkg === 'free') return 0;
    const prefix = pkg === 'premium' ? 'premium_' : '';
    return family[`${prefix}${period}_cashback_amount`];
  };

  // Get base price before any discounts (pricing model or cashback)
  const getBasePrice = (pkg, period) => {
    if (pkg === 'free') return 0;
    const prefix = pkg === 'premium' ? 'premium_' : 'pro_';
    return family[`${prefix}${period}_base_price`];
  };

  // Calculate pricing model discount amount (base price - price before cashback)
  const getPricingModelDiscount = (pkg, period) => {
    const basePrice = getBasePrice(pkg, period);
    const priceBeforeCashback = getPriceBeforeCashback(pkg, period);
    return basePrice - priceBeforeCashback;
  };

  // Calculate daily rate
  const getDailyRate = (price, isYearlyPeriod) => {
    const days = isYearlyPeriod ? 365 : 31;
    return Math.round(price / days);
  };

  const currentPeriod = isYearly ? 'yearly' : 'monthly';
  const currentDays = isYearly ? 365 : 31;

  // Feature list from shared config with dynamic activity counts
  const features = getFeatureComparison(t, 'plan_features', activityCounts);

  // Handle submit for a specific package
  const handlePackageSubmit = (pkg) => {
    if (pkg === 'free') {
      onContinueWithFree();
      return;
    }

    setIsSubmitting(true);

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/payments';

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    if (csrfToken) {
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }

    const addField = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };

    addField('payment[child_id]', child.id);
    addField('payment[subscription_days]', currentDays);
    addField('payment[subscription_package]', pkg);
    addField('payment[redirect_context]', 'wizard');
    addField('payment[order_description]',
      `Thuê bao ${pkg === 'premium' ? 'Premium' : 'Pro'} cho ${child.username} - ${currentDays} ngày`
    );

    document.body.appendChild(form);
    form.submit();
  };

  // Pricing card component with horizontal layout for vertical stacking
  const PricingCard = ({ pkg, badge }) => {
    const isFree = pkg === 'free';
    const isPremiumPkg = pkg === 'premium';

    const price = getPrice(pkg, currentPeriod);
    const basePrice = getBasePrice(pkg, currentPeriod);
    const pricingModelDiscount = getPricingModelDiscount(pkg, currentPeriod);
    const cashback = getCashbackAmount(pkg, currentPeriod);
    const dailyRate = isFree ? 0 : getDailyRate(price, isYearly);

    // Check if we have any discounts
    const hasDiscounts = !isFree && (pricingModelDiscount > 0 || cashback > 0);

    // Determine colors based on package
    const colors = isFree
      ? SUBSCRIPTION_COLORS.free
      : (isPremiumPkg ? SUBSCRIPTION_COLORS.premium : SUBSCRIPTION_COLORS.pro);

    // Get plan name for display
    const planName = isFree ? t('common.badge_free') : (isPremiumPkg ? 'Premium' : 'Pro');

    // Get button label
    const buttonLabel = isFree
      ? (t('wizard.subscription.continue_free') || 'Tiếp tục Miễn phí')
      : (t('wizard.subscription.subscribe_to_plan', { plan: planName }) || `Đăng ký ${planName}`);

    return (
      <div
        className="card h-100 position-relative"
        style={{
          borderColor: colors.border || colors.badge.color,
          borderWidth: '2px',
          borderRadius: '16px',
          boxShadow: colors.shadow ? `0px 8px 10px 0px ${colors.shadow}, 0px 20px 25px 0px ${colors.shadow}` : 'none',
          overflow: 'visible'
        }}
        data-testid={`wizard-plan-card-${pkg}`}
      >
        {/* Badge overlapping border */}
        <div className="position-absolute start-50 translate-middle-x" style={{ top: '-12px', zIndex: 1 }}>
          <span className="badge px-3 py-1" style={{ fontSize: '12px', ...colors.badge }}>
            {badge || planName}
          </span>
        </div>
        <div className="card-body py-3 pt-4 d-flex flex-column">

          {/* Price section */}
          <div className="text-center mb-2" data-testid={`wizard-price-breakdown-${pkg}`}>
            {isFree ? (
              <div className="fs-5 fw-bold" style={{ color: colors.badge.color }} data-testid={`wizard-final-price-${pkg}`}>
                {t('common.badge_free') || 'Miễn phí'}
              </div>
            ) : hasDiscounts ? (
              <div className="small">
                {/* Original price */}
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted">{t('dashboard.index.children.subscribe_modal.original_price') || 'Giá gốc'}:</span>
                  <span className="text-decoration-line-through text-muted" data-testid={`wizard-original-price-${pkg}`}>
                    {formatCurrency(basePrice)}
                  </span>
                </div>

                {/* Pricing model discount */}
                {pricingModelDiscount > 0 && (
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted text-start">
                      <i className="bi bi-tag-fill me-1" style={{ fontSize: '10px' }}></i>
                      {discountInfo}:
                    </span>
                    <span className="text-danger text-nowrap ms-2" data-testid={`wizard-pricing-discount-${pkg}`}>
                      -{formatCurrency(pricingModelDiscount)}
                    </span>
                  </div>
                )}

                {/* Referral cashback */}
                {cashback > 0 && (
                  <div className="d-flex justify-content-between mb-1" data-testid={`wizard-cashback-amount-${pkg}`} data-cashback={cashback}>
                    <span className="text-muted text-start">
                      <i className="bi bi-gift-fill me-1" style={{ fontSize: '10px' }}></i>
                      {t('dashboard.index.children.subscribe_modal.referral_discount') || 'Giảm giá giới thiệu'}:
                    </span>
                    <span className="text-danger text-nowrap ms-2">
                      -{formatCurrency(cashback)}
                    </span>
                  </div>
                )}

                {/* Divider */}
                <hr className="my-1" />

                {/* Final price */}
                <div className="d-flex justify-content-between align-items-baseline">
                  <span className="fw-semibold">{t('dashboard.index.children.subscribe_modal.final_price') || 'Thành tiền'}:</span>
                  <div className="text-end">
                    <span className="fs-5 fw-bold" style={{ color: colors.badge.color }} data-testid={`wizard-final-price-${pkg}`}>
                      {formatCurrency(price)}
                    </span>
                    <div className="text-muted" style={{ fontSize: '11px' }}>
                      (~{formatNumber(dailyRate)}₫/{t('dashboard.index.children.subscribe_modal.per_day') || 'ngày'})
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="fs-5 fw-bold" style={{ color: colors.badge.color }} data-testid={`wizard-final-price-${pkg}`}>
                  {formatCurrency(price)}
                </div>
                <div className="text-muted" style={{ fontSize: '11px' }}>
                  (~{formatNumber(dailyRate)}₫/{t('dashboard.index.children.subscribe_modal.per_day') || 'ngày'})
                </div>
              </div>
            )}
          </div>

          {/* Feature list - 2 column grid */}
          <ul className="list-unstyled small mb-3 flex-grow-1">
            {features.map((feature) => {
              const isIncluded = isFree
                ? feature.free
                : (isPremiumPkg ? feature.premium : feature.pro);
              return (
                <li key={feature.key} className="mb-1 d-flex align-items-start">
                  {isIncluded ? (
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1" style={{ fontSize: '12px' }}></i>
                  ) : (
                    <i className="bi bi-x-circle-fill text-danger me-2 mt-1" style={{ fontSize: '12px' }}></i>
                  )}
                  <span className={isIncluded ? '' : 'text-muted'} style={{ fontSize: '11px' }}>{feature.label}</span>
                </li>
              );
            })}
          </ul>

          {/* Action button */}
          <button
            type="button"
            className="btn btn-sm w-100 mt-auto"
            style={{
              ...(isFree
                ? colors.button
                : (isPremiumPkg
                  ? { background: colors.buttonGradient, color: colors.button.color }
                  : colors.button)),
              border: 'none',
              borderRadius: '12px',
              padding: '12px 16px'
            }}
            onClick={() => handlePackageSubmit(pkg)}
            disabled={isSubmitting}
            data-testid={`wizard-plan-btn-${pkg}`}
          >
            {isSubmitting ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              <>
                {buttonLabel}
                {!isFree && <i className="bi bi-arrow-right ms-2"></i>}
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  // If already subscribed, show simpler UI with continue button
  if (isSubscribed) {
    const currentPkg = child.subscription_package || 'pro';
    const colors = currentPkg === 'premium' ? SUBSCRIPTION_COLORS.premium : SUBSCRIPTION_COLORS.pro;

    return (
      <div>
        <h4 className="mb-3" data-testid="wizard-step-title">
          {t('wizard.subscription.title')}
        </h4>

        {/* Current subscription status */}
        <div className="alert border mb-4" style={{ backgroundColor: colors.badge.background }} data-testid="subscription-status">
          <div className="d-flex align-items-center">
            <span className="badge me-2" style={colors.badge}>
              {currentPkg === 'premium' ? 'Premium' : t('common.badge_pro')}
            </span>
            <span>{t('wizard.subscription.current_status')}: <strong>{child.username}</strong></span>
          </div>
          <p className="text-muted small mb-0 mt-2">
            {t('wizard.subscription.pro_tier_description')}
          </p>
        </div>

        {/* Features list */}
        <div className="card mb-4" style={{ borderColor: colors.badge.color }}>
          <div className="card-body">
            <div className="d-flex align-items-center mb-3">
              <i className="bi bi-check-circle-fill me-2 fs-5" style={{ color: colors.badge.color }}></i>
              <h5 className="mb-0">{t('wizard.subscription.pro_active_title')}</h5>
            </div>
            <ul className="list-unstyled small">
              {features.map((feature) => {
                const isIncluded = currentPkg === 'premium' ? feature.premium : feature.pro;
                return (
                  <li key={feature.key} className="mb-2 d-flex align-items-start">
                    {isIncluded ? (
                      <i className="bi bi-check-circle-fill text-success me-2 mt-1" style={{ fontSize: '14px' }}></i>
                    ) : (
                      <i className="bi bi-x-circle-fill text-danger me-2 mt-1" style={{ fontSize: '14px' }}></i>
                    )}
                    <span className={isIncluded ? '' : 'text-muted'}>{feature.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Action buttons */}
        <div className="d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onBack}
          >
            {t('common.back')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onContinue}
            data-testid="continue-btn"
          >
            {t('wizard.subscription.select_activities')}
          </button>
        </div>
      </div>
    );
  }

  // Not subscribed - show 3 plan options
  return (
    <div data-testid="wizard-subscription-step">
      <h4 className="mb-3" data-testid="wizard-step-title">
        {t('wizard.subscription.title')}
      </h4>

      <p className="text-muted text-center mb-3">
        {t('wizard.subscription.choose_plan_description') || 'Chọn gói phù hợp cho'}
        {' '}<strong>{child.username}</strong>
      </p>

      {/* Monthly/Yearly Toggle */}
      <div className="d-flex justify-content-center align-items-center gap-3 mb-4">
        <span className={`fw-semibold ${!isYearly ? 'text-primary' : 'text-muted'}`}>
          {t('dashboard.index.children.subscribe_modal.monthly') || 'Tháng'}
        </span>
        <div className="form-check form-switch mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="wizardBillingToggle"
            checked={isYearly}
            onChange={() => setIsYearly(!isYearly)}
            style={{ width: '3rem', height: '1.5rem', cursor: 'pointer' }}
          />
        </div>
        <span className={`fw-semibold ${isYearly ? 'text-primary' : 'text-muted'}`}>
          {t('dashboard.index.children.subscribe_modal.yearly') || 'Năm'}
        </span>
        <span className="badge bg-primary">
          {t('dashboard.index.children.subscribe_modal.save_percent', { percent: family.yearly_subscription_discount || 20 }) || `Tiết kiệm ${family.yearly_subscription_discount || 20}%`}
        </span>
      </div>

      {/* Pricing Cards - always vertical stack */}
      <div className="row g-3 mb-4" style={{ marginTop: '12px' }}>
        <div className="col-12">
          <PricingCard
            pkg="free"
            badge={t('wizard.subscription.basic_badge') || 'Cơ Bản'}
          />
        </div>
        <div className="col-12">
          <PricingCard
            pkg="pro"
            badge={t('dashboard.index.children.subscribe_modal.most_popular') || 'Phổ Biến Nhất'}
          />
        </div>
        <div className="col-12">
          <PricingCard
            pkg="premium"
            badge={t('dashboard.index.children.subscribe_modal.most_complete') || 'Đầy Đủ Nhất'}
          />
        </div>
      </div>

      {/* Gift Code Section */}
      <div className="text-center mb-4">
        <GiftCodeSection
          childId={child.id}
          onRedeemSuccess={onContinue}
          t={t}
        />
      </div>

      {/* Back button only - action buttons are now inside each card */}
      <div className="d-flex justify-content-start">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onBack}
          disabled={isSubmitting}
        >
          {t('common.back')}
        </button>
      </div>
    </div>
  );
};
